import React from 'react';
import {
    MDBBtn,
    MDBDropdown,
    MDBDropdownMenu,
    MDBDropdownToggle,
    MDBDropdownItem,
    MDBInput,
} from 'mdb-react-ui-kit';
import { connect } from 'react-redux';
import Loader from '../Loader';
import Modal from '../Modal';
import axios from 'axios';
import moment from 'moment';
import debounce from 'debounce';
import { baseURL } from '../api/baseURL';
import AdminShell from './layout/AdminShell';
import { countPillStyle, thStyle } from './layout/listUi';

type PhotographerOption = { _id: string, name: string, email: string };
type CustomerOption = { _id: string, name: string, email: string };

type HistoryRow = {
    _id: string,
    title: string,
    message: string,
    audienceLabel: string,
    reachCount: number,
    sentAt: string,
};

type State = {
    requested: boolean,
    err: boolean,
    successfulNotification: boolean,
    topic: string,
    title: string,
    message: string,
    imageUrl: string,
    history: HistoryRow[],
    loadingHistory: boolean,
    targetMode: 'broadcast' | 'single_photographer' | 'single_customer',
    photographerSearch: string,
    photographerResults: PhotographerOption[],
    searchingPhotographers: boolean,
    selectedPhotographer: PhotographerOption | null,
    customerSearch: string,
    customerResults: CustomerOption[],
    searchingCustomers: boolean,
    selectedCustomer: CustomerOption | null,
    noDeviceToken: boolean,
};

let topicMapper: any = {
    "all_users_snapwiz": "Snapwiz",
    "all_users_snapmaster": "Snapmaster",
    "all_users": "Both App",
}

const APP_LABEL_FOR_TOPIC: Record<string, string> = {
    all_users_snapwiz: "SnapWiz",
    all_users_snapmaster: "SnapMaster",
    all_users: "SnapWiz + SnapMaster",
};

/** Send Notification page — routed at /notify. Phase 7 of the redesign adds
 * a live phone preview and a "recently sent" history table backed by a new
 * NotificationBroadcast log (Admin Server) — nothing recorded a send before
 * this. Reach counts are a real device_token-count snapshot at send time,
 * not a delivery/open confirmation (FCM's topic-send API doesn't report
 * that). */
class PushNotifications extends React.Component<{ token?: string }, State> {

    state: State = {
        requested: false,
        err: false,
        successfulNotification: false,
        topic: "",
        title: "",
        message: "",
        imageUrl: "",
        history: [],
        loadingHistory: false,
        targetMode: 'broadcast',
        photographerSearch: '',
        photographerResults: [],
        searchingPhotographers: false,
        selectedPhotographer: null,
        customerSearch: '',
        customerResults: [],
        searchingCustomers: false,
        selectedCustomer: null,
        noDeviceToken: false,
    }

    componentDidMount(): void {
        this.fetchHistory();
    }

    fetchHistory = (): void => {
        const token = this.props.token;
        if (!token) return;
        this.setState({ loadingHistory: true });
        axios.get(`${baseURL}/notification-history`, { headers: { Authorization: `Bearer ${token}` } })
            .then((response: any) => {
                this.setState({ history: response?.data?.history || [], loadingHistory: false });
            })
            .catch(() => {
                this.setState({ loadingHistory: false });
            });
    }

    searchPhotographers = (searchText: string): void => {
        const token = this.props.token;
        if (!token) return;
        this.setState({ searchingPhotographers: true });
        axios.get(`${baseURL}/photographers?search=${encodeURIComponent(searchText)}&limit=10`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then((response: any) => {
                this.setState({ photographerResults: response?.data?.photographers || [], searchingPhotographers: false });
            })
            .catch(() => {
                this.setState({ photographerResults: [], searchingPhotographers: false });
            });
    }

    onPhotographerSearchChange = debounce((searchText: string) => {
        if (searchText.length > 2) {
            this.searchPhotographers(searchText);
        } else {
            this.setState({ photographerResults: [] });
        }
    }, 200);

    searchCustomers = (searchText: string): void => {
        const token = this.props.token;
        if (!token) return;
        this.setState({ searchingCustomers: true });
        axios.get(`${baseURL}/customers?search=${encodeURIComponent(searchText)}&limit=10`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then((response: any) => {
                this.setState({ customerResults: response?.data?.users || [], searchingCustomers: false });
            })
            .catch(() => {
                this.setState({ customerResults: [], searchingCustomers: false });
            });
    }

    onCustomerSearchChange = debounce((searchText: string) => {
        if (searchText.length > 2) {
            this.searchCustomers(searchText);
        } else {
            this.setState({ customerResults: [] });
        }
    }, 200);

    sendAdminPushNotification = () => {
        if (this.state.requested) return;
        if (this.state.targetMode === 'single_photographer' && !this.state.selectedPhotographer) return;
        if (this.state.targetMode === 'single_customer' && !this.state.selectedCustomer) return;

        this.setState({ err: false, requested: true, noDeviceToken: false });
        const body: Record<string, unknown> = {
            title: this.state.title,
            message: this.state.message,
        };
        if (this.state.targetMode === 'single_photographer') {
            body.photographerId = this.state.selectedPhotographer!._id;
        } else if (this.state.targetMode === 'single_customer') {
            body.customerId = this.state.selectedCustomer!._id;
        } else {
            body.topic = this.state.topic;
        }
        axios.post(`${baseURL}/send-admin-push-notification`, body, {
            headers: { Authorization: `Bearer ${this.props.token}` }
        })
            .then((response: any) => {
                if (response?.data?.message === "success") {
                    const alertOnly = response?.data?.reason === "no_device_token_alert_only";
                    this.setState({
                        successfulNotification: true, err: false, requested: false, noDeviceToken: alertOnly,
                        title: "", message: "", topic: "",
                        selectedPhotographer: null, photographerSearch: '', photographerResults: [],
                        selectedCustomer: null, customerSearch: '', customerResults: [],
                    });
                    this.fetchHistory();
                } else if (response?.data?.reason === "no_device_token") {
                    this.setState({ requested: false, noDeviceToken: true });
                } else {
                    this.setState({ err: true, requested: false });
                }
            })
            .catch(() => {
                this.setState({ err: true, requested: false });
            })
    }

    toggleModal = () => {
        if (this.state.err) {
            this.setState({ err: false });
        }

        if (this.state.successfulNotification) {
            this.setState({ successfulNotification: false });
        }

        if (this.state.noDeviceToken) {
            this.setState({ noDeviceToken: false });
        }
    }

    render() {
        const { title, message, topic, history, loadingHistory, targetMode, selectedPhotographer, photographerSearch, photographerResults, searchingPhotographers, selectedCustomer, customerSearch, customerResults, searchingCustomers } = this.state;
        const previewAppLabel = targetMode === 'single_photographer'
            ? 'SnapMaster'
            : targetMode === 'single_customer'
                ? 'SnapWiz'
                : (topic ? APP_LABEL_FOR_TOPIC[topic] : 'SnapWiz');
        const canSend = !!title && !!message && (
            targetMode === 'single_photographer' ? !!selectedPhotographer
                : targetMode === 'single_customer' ? !!selectedCustomer
                    : !!topic
        );

        return (
            <AdminShell activePage="notify" title="Send notification" subtitle="Broadcast a push notification to app users">
                <Modal open={this.state.err || this.state.successfulNotification || this.state.noDeviceToken} hideSec primaryText="Okay" title='Update' onConfirm={this.toggleModal} toggleOpen={this.toggleModal} content={<h6>{this.state.successfulNotification && this.state.noDeviceToken ? "Saved as an in-app alert — this person has no registered device, so they won't get a push until they next open the app." : this.state.successfulNotification ? "Notification was sent to selected app users" : "Something went wrong, please try again later"}</h6>} />
                {this.state.requested ? <Loader /> : null}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 16, alignItems: 'start', marginBottom: 16 }}>
                    <div className="card">
                        <div className="card-body">
                            <MDBInput contrast label="Title" id="title" type="text" maxLength={50} value={title} onChange={(e) => { this.setState({ title: e.target.value }) }} />
                            <div style={{ fontSize: 10.5, opacity: 0.5, textAlign: 'right', marginTop: 2 }}>{title.length}/50</div>
                            <br />
                            <MDBInput contrast label="Message" id="message" type="text" maxLength={150} value={message} onChange={(e) => { this.setState({ message: e.target.value }) }} />
                            <div style={{ fontSize: 10.5, opacity: 0.5, textAlign: 'right', marginTop: 2 }}>{message.length}/150</div>
                            <br />

                            <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                                <MDBBtn
                                    size="sm"
                                    color={targetMode === 'broadcast' ? 'primary' : 'secondary'}
                                    onClick={() => this.setState({ targetMode: 'broadcast', selectedPhotographer: null, photographerSearch: '', photographerResults: [], selectedCustomer: null, customerSearch: '', customerResults: [] })}
                                >
                                    Broadcast (all users)
                                </MDBBtn>
                                <MDBBtn
                                    size="sm"
                                    color={targetMode === 'single_photographer' ? 'primary' : 'secondary'}
                                    onClick={() => this.setState({ targetMode: 'single_photographer', topic: '', selectedCustomer: null, customerSearch: '', customerResults: [] })}
                                >
                                    Single photographer
                                </MDBBtn>
                                <MDBBtn
                                    size="sm"
                                    color={targetMode === 'single_customer' ? 'primary' : 'secondary'}
                                    onClick={() => this.setState({ targetMode: 'single_customer', topic: '', selectedPhotographer: null, photographerSearch: '', photographerResults: [] })}
                                >
                                    Single customer
                                </MDBBtn>
                            </div>

                            {targetMode === 'broadcast' ? (
                                <MDBDropdown>
                                    <MDBDropdownToggle>{topic ? topicMapper[topic] : "Select App"}</MDBDropdownToggle>
                                    <MDBDropdownMenu>
                                        <MDBDropdownItem link onClick={() => { this.setState({ topic: "all_users_snapwiz" }) }}>Snapwiz</MDBDropdownItem>
                                        <MDBDropdownItem link onClick={() => { this.setState({ topic: "all_users_snapmaster" }) }}>Snapmaster</MDBDropdownItem>
                                        <MDBDropdownItem link onClick={() => { this.setState({ topic: "all_users" }) }}>Both</MDBDropdownItem>
                                    </MDBDropdownMenu>
                                </MDBDropdown>
                            ) : targetMode === 'single_photographer' ? (
                                <div>
                                    {selectedPhotographer ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, background: 'rgba(0,144,231,0.12)', border: '1px solid rgba(0,144,231,0.35)' }}>
                                            <span style={{ fontSize: 13, fontWeight: 700 }}>{selectedPhotographer.name}</span>
                                            <span style={{ fontSize: 11.5, opacity: 0.7 }}>{selectedPhotographer.email}</span>
                                            <span style={{ marginLeft: 'auto', cursor: 'pointer', fontSize: 12, color: '#0090e7' }} onClick={() => this.setState({ selectedPhotographer: null })}>Change</span>
                                        </div>
                                    ) : (
                                        <div style={{ position: 'relative' }}>
                                            <input
                                                className="form-control"
                                                placeholder="Search photographer by name or email…"
                                                value={photographerSearch}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    this.setState({ photographerSearch: v });
                                                    this.onPhotographerSearchChange(v);
                                                }}
                                            />
                                            {searchingPhotographers ? (
                                                <div style={{ fontSize: 11.5, opacity: 0.6, marginTop: 4 }}>Searching…</div>
                                            ) : photographerResults.length > 0 ? (
                                                <div style={{ marginTop: 6, border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, maxHeight: 220, overflowY: 'auto' }}>
                                                    {photographerResults.map((p) => (
                                                        <div
                                                            key={p._id}
                                                            onClick={() => this.setState({ selectedPhotographer: p, photographerSearch: '', photographerResults: [] })}
                                                            style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid rgba(255,255,255,0.06)' }}
                                                        >
                                                            <div style={{ fontWeight: 700 }}>{p.name}</div>
                                                            <div style={{ fontSize: 11.5, opacity: 0.65 }}>{p.email}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : photographerSearch.length > 2 ? (
                                                <div style={{ fontSize: 11.5, opacity: 0.6, marginTop: 4 }}>No matching photographers</div>
                                            ) : null}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div>
                                    {selectedCustomer ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, background: 'rgba(0,144,231,0.12)', border: '1px solid rgba(0,144,231,0.35)' }}>
                                            <span style={{ fontSize: 13, fontWeight: 700 }}>{selectedCustomer.name}</span>
                                            <span style={{ fontSize: 11.5, opacity: 0.7 }}>{selectedCustomer.email}</span>
                                            <span style={{ marginLeft: 'auto', cursor: 'pointer', fontSize: 12, color: '#0090e7' }} onClick={() => this.setState({ selectedCustomer: null })}>Change</span>
                                        </div>
                                    ) : (
                                        <div style={{ position: 'relative' }}>
                                            <input
                                                className="form-control"
                                                placeholder="Search customer by name or email…"
                                                value={customerSearch}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    this.setState({ customerSearch: v });
                                                    this.onCustomerSearchChange(v);
                                                }}
                                            />
                                            {searchingCustomers ? (
                                                <div style={{ fontSize: 11.5, opacity: 0.6, marginTop: 4 }}>Searching…</div>
                                            ) : customerResults.length > 0 ? (
                                                <div style={{ marginTop: 6, border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, maxHeight: 220, overflowY: 'auto' }}>
                                                    {customerResults.map((c) => (
                                                        <div
                                                            key={c._id}
                                                            onClick={() => this.setState({ selectedCustomer: c, customerSearch: '', customerResults: [] })}
                                                            style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid rgba(255,255,255,0.06)' }}
                                                        >
                                                            <div style={{ fontWeight: 700 }}>{c.name}</div>
                                                            <div style={{ fontSize: 11.5, opacity: 0.65 }}>{c.email}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : customerSearch.length > 2 ? (
                                                <div style={{ fontSize: 11.5, opacity: 0.6, marginTop: 4 }}>No matching customers</div>
                                            ) : null}
                                        </div>
                                    )}
                                </div>
                            )}

                            <br />
                            <MDBBtn onClick={() => { this.sendAdminPushNotification() }} disabled={!canSend}>
                                Send notification
                            </MDBBtn>
                        </div>
                    </div>

                    {/* Live phone preview — matches the mockup's phone-frame concept, updates on every keystroke */}
                    <div className="card">
                        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div style={{ fontSize: 10.5, opacity: 0.5, fontWeight: 700, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Preview</div>
                            <div style={{
                                width: '100%', maxWidth: 280, borderRadius: 16, border: '1px solid rgba(255,255,255,0.12)',
                                background: 'rgba(255,255,255,0.04)', padding: 14,
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                    <div style={{ width: 20, height: 20, borderRadius: 5, background: '#0090e7', flexShrink: 0 }} />
                                    <span style={{ fontSize: 11, fontWeight: 700, opacity: 0.7 }}>{previewAppLabel}</span>
                                    <span style={{ fontSize: 10, opacity: 0.4, marginLeft: 'auto' }}>now</span>
                                </div>
                                <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 3 }}>
                                    {title || <span style={{ opacity: 0.35, fontWeight: 600 }}>Notification title</span>}
                                </div>
                                <div style={{ fontSize: 12, opacity: 0.75, lineHeight: 1.4 }}>
                                    {message || <span style={{ opacity: 0.35 }}>Notification message will appear here as you type</span>}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body">
                        <h4 className="card-title">
                            Recently sent
                            {history.length ? <span style={countPillStyle}>{history.length}</span> : null}
                        </h4>
                        {loadingHistory ? (
                            <p style={{ fontSize: 13, color: '#999' }}>Loading…</p>
                        ) : history.length === 0 ? (
                            <p style={{ fontSize: 13, color: '#999' }}>No notifications sent yet.</p>
                        ) : (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', fontSize: 12.5, borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr>
                                            <th style={thStyle}>Header</th>
                                            <th style={thStyle}>Audience</th>
                                            <th style={thStyle}>Sent</th>
                                            <th style={thStyle}>Reach</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {history.map((row) => (
                                            <tr key={row._id} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                                                <td style={{ padding: '10px 12px' }}>
                                                    <div style={{ fontWeight: 700 }}>{row.title}</div>
                                                    <div style={{ fontSize: 11, opacity: 0.6 }}>{row.message}</div>
                                                </td>
                                                <td style={{ padding: '10px 12px' }}>{row.audienceLabel}</td>
                                                <td style={{ padding: '10px 12px', opacity: 0.75 }}>{moment(new Date(row.sentAt)).fromNow()}</td>
                                                <td style={{ padding: '10px 12px', fontWeight: 700 }}>{row.reachCount.toLocaleString('en-IN')}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </AdminShell>
        );
    }
}

const mapDispatchToProps = (dispatch: Function) => {
    return {
        dispatch
    }
}

const mapStateToProps = (state: any, ownProps: any) => {
    return {
        token: state.auth.token
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(PushNotifications);
